import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';

export interface Board {
  id: string;
  title: string;
  created_at: string;
  user_id: string;
  is_default?: boolean;
}

interface BoardState {
  boards: Board[];
  currentBoard: Board | null;
  loading: boolean;
  fetchBoards: () => Promise<void>;
  createBoard: (title: string) => Promise<void>;
  updateBoard: (id: string, title: string) => Promise<void>;
  deleteBoard: (id: string) => Promise<void>;
  setCurrentBoard: (board: Board | null) => void;
  ensureDefaultBoard: () => Promise<void>;
}

export const useBoardStore = create<BoardState>((set, get) => ({
  boards: [],
  currentBoard: null,
  loading: false,

  fetchBoards: async () => {
    set({ loading: true });
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('boards')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: true });

        if (error) throw error;

        const boards = data || [];
        set({ boards });

        // If there's no current board set, set the first board as current
        if (!get().currentBoard && boards.length > 0) {
          set({ currentBoard: boards[0] });
        }
      });
    } catch (error) {
      handleException(error);
    } finally {
      set({ loading: false });
    }
  },

  createBoard: async (title: string) => {
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('boards')
          .insert([
            {
              title,
              user_id: userId,
              is_default: false,
            },
          ])
          .select()
          .single();

        if (error) throw error;

        set((state) => ({
          boards: [...state.boards, data],
          currentBoard: data, // Set the new board as current
        }));
      });
    } catch (error) {
      handleException(error);
    }
  },

  updateBoard: async (id: string, title: string) => {
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('boards')
          .update({ title })
          .match({ id, user_id: userId })
          .select()
          .single();

        if (error) throw error;

        set((state) => ({
          boards: state.boards.map((board) => (board.id === id ? data : board)),
          currentBoard: state.currentBoard?.id === id ? data : state.currentBoard,
        }));
      });
    } catch (error) {
      handleException(error);
    }
  },

  deleteBoard: async (id: string) => {
    try {
      await withAuthUser(async (userId) => {
        // Don't allow deleting the default board
        const boardToDelete = get().boards.find((b) => b.id === id);
        if (boardToDelete?.is_default) {
          throw new Error("Cannot delete the default board");
        }

        const { error } = await supabase
          .from('boards')
          .delete()
          .match({ id, user_id: userId });

        if (error) throw error;

        // Update state
        set((state) => {
          const newBoards = state.boards.filter((board) => board.id !== id);
          const newCurrentBoard = state.currentBoard?.id === id
            ? newBoards[0] || null
            : state.currentBoard;

          return {
            boards: newBoards,
            currentBoard: newCurrentBoard,
          };
        });
      });
    } catch (error) {
      handleException(error);
    }
  },

  setCurrentBoard: (board) => {
    set({ currentBoard: board });
  },

  ensureDefaultBoard: async () => {
    try {
      await withAuthUser(async (userId) => {
        // Check if user has any boards
        const { data: boards, error: fetchError } = await supabase
          .from('boards')
          .select('*')
          .eq('user_id', userId);

        if (fetchError) throw fetchError;

        // If no boards exist, create a default one
        if (!boards || boards.length === 0) {
          const { data: newBoard, error: createError } = await supabase
            .from('boards')
            .insert([
              {
                title: 'My Board',
                user_id: userId,
                is_default: true,
              },
            ])
            .select()
            .single();

          if (createError) throw createError;

          set({
            boards: [newBoard],
            currentBoard: newBoard,
          });
        }
      });
    } catch (error) {
      handleException(error);
    }
  },
}));
